/**
 * POSOrderCompleteScreen - Order completion display with fiscal receipt integration
 * Shows order number, total, fiscal invoice status, and new order button
 */
import { Check, X, QrCode, Printer, AlertCircle, GraduationCap, Receipt, Ban, History, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FiscalReceiptDisplay, printFiscalReceipt } from '@/components/frcs/FiscalReceipt';
import { posApi } from '@/utils/apiClient';
import { toast } from 'sonner';

export default function POSOrderCompleteScreen({ 
  orderComplete, 
  fiscalInvoice, 
  showFiscalReceipt, 
  setShowFiscalReceipt,
  onStartNewOrder,
  onShowHistory,
  receiptPrinted = true  // Default to true for backward compatibility
}) {
  // Print customer receipt on demand
  const handlePrintReceipt = async () => {
    if (!orderComplete.order_id) {
      toast.error('Order ID not available');
      return;
    }
    try {
      await posApi.post(`/pos/print-customer-receipt/${orderComplete.order_id}`);
      toast.success('Receipt sent to printer');
    } catch (err) {
      console.error('Print receipt error:', err);
      toast.error('Failed to print receipt');
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-8 ${
      orderComplete.is_training 
        ? 'bg-gradient-to-b from-amber-800 to-amber-900' 
        : 'bg-gradient-to-b from-teal-900 to-teal-950'
    }`} data-testid="pos-order-complete">
      {/* Training Mode Banner */}
      {orderComplete.is_training && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-3 px-4 z-50 flex items-center justify-center gap-2">
          <GraduationCap className="w-6 h-6" />
          <span className="font-bold text-lg">TRAINING MODE</span>
          <span className="text-amber-100">- This is NOT a fiscal invoice</span>
        </div>
      )}
      
      {showFiscalReceipt && fiscalInvoice ? (
        <div className="bg-white rounded-2xl p-4 max-w-md mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">Fiscal Receipt</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowFiscalReceipt(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <FiscalReceiptDisplay 
            fiscalInvoice={fiscalInvoice}
            onPrint={() => printFiscalReceipt(fiscalInvoice)}
          />
          <div className="mt-4 flex gap-3">
            <Button onClick={() => setShowFiscalReceipt(false)} variant="outline" className="flex-1">Back</Button>
            <Button onClick={onStartNewOrder} className="flex-1 bg-teal-600 hover:bg-teal-700">New Order</Button>
          </div>
        </div>
      ) : (
        <div className="text-center text-white">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-teal-500 flex items-center justify-center animate-bounce">
            <Check className="w-16 h-16" />
          </div>
          <h1 className="text-5xl font-bold mb-4">Order Complete!</h1>
          <div className="bg-white/10 rounded-3xl p-8 inline-block mb-6">
            <div className="text-lg text-teal-200 mb-2">Order Number</div>
            <div className="text-8xl font-bold">{orderComplete.order_number}</div>
          </div>
          <p className="text-2xl mb-2">Total: ${(orderComplete.total || 0).toFixed(2)}</p>
          
          {orderComplete.fiscal_success ? (
            <div className="bg-green-500/20 border border-green-400 rounded-xl px-4 py-3 mb-4 inline-block">
              <div className="flex items-center gap-2 text-green-300">
                <QrCode className="w-5 h-5" />
                <span className="font-medium">FRCS Fiscal Invoice Generated</span>
              </div>
              <p className="text-sm text-green-200 mt-1">SDC: {orderComplete.sdc_number}</p>
            </div>
          ) : (
            <div className="bg-yellow-500/20 border border-yellow-400 rounded-xl px-4 py-3 mb-4 inline-block">
              <div className="flex items-center gap-2 text-yellow-300">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Fiscal Invoice Pending</span>
              </div>
            </div>
          )}
          
          {receiptPrinted ? (
            <p className="text-teal-200 mb-6">
              <Receipt className="w-5 h-5 inline mr-2" />
              Receipt sent to printer
            </p>
          ) : (
            <p className="text-teal-200/70 mb-6">
              <Ban className="w-5 h-5 inline mr-2" />
              Receipt skipped
            </p>
          )}
          
          <div className="flex flex-col gap-3 max-w-md mx-auto">
            {fiscalInvoice && (
              <>
                <Button 
                  onClick={() => setShowFiscalReceipt(true)}
                  variant="outline"
                  className="w-full py-4 text-lg bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="pos-view-fiscal-btn"
                >
                  <QrCode className="w-5 h-5 mr-2" /> View Fiscal Receipt
                </Button>
                <Button 
                  onClick={() => printFiscalReceipt(fiscalInvoice)}
                  variant="outline"
                  className="w-full py-4 text-lg bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="pos-print-fiscal-btn"
                >
                  <Printer className="w-5 h-5 mr-2" /> Print Fiscal Receipt
                </Button>
              </>
            )}
            
            {/* Print Invoice - allows reprinting if customer changes mind */}
            <Button 
              onClick={handlePrintReceipt}
              variant="outline"
              className="w-full py-4 text-lg bg-white/10 border-white/30 text-white hover:bg-white/20"
              data-testid="pos-print-invoice-btn"
            >
              <FileText className="w-5 h-5 mr-2" /> Print Invoice
            </Button>
            
            {/* History - view past orders */}
            {onShowHistory && (
              <Button 
                onClick={onShowHistory}
                variant="outline"
                className="w-full py-4 text-lg bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="pos-history-btn"
              >
                <History className="w-5 h-5 mr-2" /> Order History
              </Button>
            )}
            
            <Button 
              onClick={onStartNewOrder}
              className="w-full py-6 text-xl bg-teal-500 hover:bg-teal-600"
              data-testid="pos-new-order-btn"
            >
              Start New Order
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
